// libs
import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Box } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import { ModalVariant } from '@/components/Modals/types';
import { useAppConfig } from '@/hooks/useAppConfig';
import useDebounceState from '@/hooks/useDebounceState';

import LocalisedSttingInfo from '../../../course/views/CourseBuilder/components/SidebarDetails/Settings/components/Dialogs/components/Certificate/localizedSettingInfo';
import CategoryList from '../../views/Category/components/CategoryList';
import useCategories from '../../views/Category/hooks/useCategories';
import useCategoryActions from '../../views/Category/hooks/useCategoryActions';
import { filterCategoriesByText } from '../../views/Category/utils';
import EmptyState from './EmptyState';

interface AttachCategoriesModalProps {
  contentType: string;
  selectedCategoryIds?: string[] | null;
  onAttach: (categoryIds: string[]) => void;
  onClose: () => void;
  subWorkspaceIds?: string[];
  readonly?: boolean;
}

function AttachCategoriesModal(props: AttachCategoriesModalProps) {
  const { t } = useTranslation('Discover');
  const { contentType, selectedCategoryIds, onAttach, onClose, subWorkspaceIds, readonly } = props;
  const [searchInput, setSearchInput, searchString] = useDebounceState('');
  const {
    site: { subWorkspacesEnabled },
  } = useAppConfig();
  const { fetching, categoriesTree } = useCategories(
    subWorkspacesEnabled ? { crossSubWorkspace: true, subWorkspaceIds } : undefined,
  );
  const { layoutView, actions, selectedItems, state } = useCategoryActions({
    layoutView: 'multi-select',
    selectedIds: selectedCategoryIds ?? [],
  });

  const attatchCategories = useCallback(() => {
    onAttach(selectedItems);
    onClose();
  }, [onClose, onAttach, selectedItems]);

  const filteredCategories = useMemo(
    () => filterCategoriesByText(categoriesTree, searchString),
    [categoriesTree, searchString],
  );

  return (
    <ContentListModal.Main
      action={
        readonly
          ? undefined
          : {
              onSubmit: attatchCategories,
              text: t('Common:Actions.Attach'),
            }
      }
      footer={readonly ? <LocalisedSttingInfo /> : undefined}
      header={{
        title: t('Discover:Category.Admin.Title'),
        description: t('Discover:Category.Admin.AttachCategoryModalDescription', { contentType }),
      }}
      searchBar={{
        value: searchInput,
        onChange: setSearchInput,
        selectedCount: selectedItems.length,
        placeholder: t('Common:Search'),
      }}
      variant={ModalVariant.Wide}
      onClose={onClose}>
      {filteredCategories?.length ? (
        <Box className="px-24 pb-32" style={{ pointerEvents: readonly ? 'none' : 'auto' }}>
          <CategoryList
            actions={actions}
            categoriesTree={filteredCategories}
            fetching={fetching}
            layoutView={layoutView}
            state={state}
          />
        </Box>
      ) : fetching ? (
        <Loader />
      ) : (
        <EmptyState searchText={searchString} />
      )}
    </ContentListModal.Main>
  );
}

export default AttachCategoriesModal;
